import * as React from "react";
import { useParams } from "react-router-dom";
import { Typography, Grid, makeStyles, Paper } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import Skeleton from "@material-ui/lab/Skeleton";
import { useQuery, useMutation } from "@apollo/client";

import { ConstrainedPageContent, Page, PageContent, StretchedContent } from "./components/layout";
import { StoryComments, StoryLinkedItems } from "./components/stories";
import WrapUpStoryHeader from "./components/wrapUpStories/WrapUpStoryHeader";
import WrapUpStoryInfo from "./components/wrapUpStories/WrapUpStoryInfo";
import { useUserContext } from "./components/auth/UserContextProvider";
import { GET_BEACON_WRAP_UP_STORY } from "../api/graphQL/queries";
import { BeaconWrapUpStory, BeaconWrapUpStoryCommentList, BeaconStory, BeaconStoryComment } from "../api/graphQL/queryModels";
import { APPEND_BEACON_WRAP_UP_STORY_COMMENT } from "../api/graphQL/mutations";
import { useNotifications } from "./components/notifications/NotificationContextProvider";
import DocumentTitle from "./components/DocumentTitle";

const useStyles = makeStyles((theme) => ({
    linkedItems: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(2)
    },
    disclaimer: {
        marginTop: theme.spacing(3)
    },
    disclaimerCard: {
        padding: theme.spacing(2, 5),
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        borderRadius: 0,
        boxShadow: theme.shadows[1]
    },
    disclaimerWrapper: {
        marginTop: theme.spacing(3)
    },
    disclaimerIcon: {
        color: theme.palette.warning.main,
        flexShrink: 0
    }
}));

const WrapUpStoryDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const { setNotification } = useNotifications();
    const classes = useStyles();

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const { data, loading, error } = useQuery<{ beaconWrapUpStory: BeaconWrapUpStory }>(GET_BEACON_WRAP_UP_STORY, {
        variables: {
            id
        }
    });

    const story = data?.beaconWrapUpStory;

    const isFetching = !story || loading;

    const { user: currentUser } = useUserContext();

    const [appendBeaconWrapUpStoryComment, { error: appendCommentError }] = useMutation<{
        beaconWrapUpStoryCommentAppend: BeaconWrapUpStoryCommentList;
    }>(APPEND_BEACON_WRAP_UP_STORY_COMMENT);

    const saveComment = async (comment: string) => {
        await appendBeaconWrapUpStoryComment({
            variables: {
                beaconWrapUpStoryComment: {
                    wrapUpStoryId: id,
                    text: comment
                }
            }
        });
    };

    React.useEffect(() => {
        if (error?.message || appendCommentError?.message) {
            setNotification(error?.message || appendCommentError?.message, "error");
        }
    }, [error, appendCommentError, setNotification]);

    if (isFetching) {
        return (
            <Page noMarginTop>
                <PageContent>
                    <StretchedContent>
                        <Skeleton variant="rect" height={200} />
                    </StretchedContent>
                    <ConstrainedPageContent>
                        <Grid container spacing={2}>
                            <Grid item />
                            <Grid item xs={12} className="avatar-placeholders">
                                <Skeleton variant="circle" width={48} height={48} />
                                <Skeleton variant="circle" width={48} height={48} />
                                <Skeleton variant="circle" width={48} height={48} />
                                <Skeleton variant="circle" width={48} height={48} />
                                <Skeleton variant="circle" width={48} height={48} />
                                <Skeleton variant="circle" width={48} height={48} />
                            </Grid>
                            <Grid item xs={12} className="image-placeholders">
                                <Skeleton variant="rect" width={96} height={128} />
                                <Skeleton variant="rect" width={96} height={128} />
                                <Skeleton variant="rect" width={96} height={128} />
                                <Skeleton variant="rect" width={96} height={128} />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h3">
                                    <Skeleton variant="text" width="45%" />
                                </Typography>
                                <Typography variant="body1">
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" />
                                    <Skeleton variant="text" width="30%" />
                                </Typography>
                            </Grid>
                        </Grid>
                    </ConstrainedPageContent>
                </PageContent>
            </Page>
        );
    }

    return (
        <>
            <DocumentTitle pageTitle={story?.title} />
            <Page noMarginTop>
                <PageContent>
                    {story && (
                        <StretchedContent>
                            <WrapUpStoryHeader story={story} />
                        </StretchedContent>
                    )}
                    {/* Remove the disclaimer when we add approved status and a story is approved for external use. */}
                    {story && !story.isApproved && (
                        <StretchedContent className={classes.disclaimerWrapper}>
                            <Paper className={classes.disclaimerCard}>
                                <WarningIcon className={classes.disclaimerIcon} />
                                <Typography variant="body1">
                                    This content is for internal use only and is <strong>not</strong> approved for external sharing.
                                </Typography>
                            </Paper>
                        </StretchedContent>
                    )}
                    {story && (
                        <ConstrainedPageContent>
                            <WrapUpStoryInfo story={story} />
                        </ConstrainedPageContent>
                    )}
                    <div className={classes.linkedItems}>
                        <Typography variant="h5" gutterBottom>
                            Linked Items
                        </Typography>
                        {story && <StoryLinkedItems story={story as unknown as BeaconStory} />}
                    </div>
                    {!!currentUser && !!story && (
                        <ConstrainedPageContent>
                            <StoryComments
                                comments={(story.commentList?.comments || []) as unknown as BeaconStoryComment[]}
                                saveComment={saveComment}
                            />
                        </ConstrainedPageContent>
                    )}
                </PageContent>
            </Page>
        </>
    );
};

export default WrapUpStoryDetailPage;
